import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomHeader } from "../components/AuthHeader";
import { CustomButton } from "../components/CustomButton";
import { useAuthStore } from "../features/auth/authStore";
import { RegisterInput } from "../features/auth/types";
import { useToast } from "../components/ToastProvider";
import { ArrowLeft } from "lucide-react";
import rushLogo from "../assets/png/rush-logo.png";

type OTPVerifyLocationState = {
  registrationData?: RegisterInput;
  phone?: string;
  intent?: "signup" | "login";
};

export default function OTPVerifyScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { verifyOtp, verifyPhone, customerRegister, isLoading, error } =
    useAuthStore();

  const locationState = (location.state as OTPVerifyLocationState) ?? {};
  const registrationData = locationState.registrationData;
  const intent = locationState.intent ?? "signup";
  const phone = locationState.phone ?? registrationData?.phone ?? "";

  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);

  const handleOtpChange = (
    index: number,
    value: string,
    inputElement: HTMLInputElement,
  ) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const nextOtp = [...otp];
    nextOtp[index] = value;
    setOtp(nextOtp);

    if (value && inputElement.nextElementSibling instanceof HTMLInputElement) {
      inputElement.nextElementSibling.focus();
    }
  };

  const handleCreateAccount = async () => {
    if (!phone) {
      showToast("Please enter mobile number first", "error");
      return;
    }

    const otpValue = "1111"; //otp.join("");

    if (otpValue.length !== 4) {
      showToast("Please enter 4 digit OTP", "error");
      return;
    }

    try {
      // await verifyOtp({ phone, otp: otpValue });

      if (intent === "login") {
        const currentPhoneNotRegistered =
          useAuthStore.getState().phoneNotRegistered;

        if (currentPhoneNotRegistered === true) {
          showToast(
            "Phone number not registered. Please sign up first.",
            "error",
          );
          return;
        }

        navigate("/home");
        return;
      }

      if (!registrationData) {
        showToast("Please sign up first", "error");
        return;
      }

      await customerRegister(registrationData);
      navigate("/home");
    } catch (verifyError) {
      const message =
        verifyError instanceof Error
          ? verifyError.message
          : error || "Verification failed";
      showToast(message, "error");
    }
  };

  const handleResendOtp = async () => {
    if (!phone) {
      showToast("Please enter mobile number first", "error");
      return;
    }

    try {
      await verifyPhone(phone);
      showToast("OTP sent successfully", "success");
    } catch (verifyError) {
      const message =
        verifyError instanceof Error
          ? verifyError.message
          : error || "Failed to resend OTP";
      showToast(message, "error");
    }
  };

  useEffect(() => {
    handleCreateAccount();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white px-6 pt-4 pb-10">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md px-6 -mx-6 pt-4 pb-4 flex items-center justify-between mb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center active:scale-95 transition-transform shadow-sm bg-white"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>

        <img
          src={rushLogo}
          alt="Rush Logo"
          className="h-7 w-auto object-contain"
        />
      </div>

      {/* Header */}
      {/* <CustomHeader
        title="Verify Your Mobile Number"
        subtitle="Please enter the 4 digit OTP sent to your phone"
      /> */}

      {/* Main Content Area */}
      <div className="mt-8 flex-1">
        {/* OTP Input Boxes */}
        {/* <div className="flex justify-center gap-3 mb-8">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(event) =>
                handleOtpChange(index, event.target.value, event.currentTarget)
              }
              disabled={isLoading}
              className="w-14 h-14 bg-gray-50 border border-gray-300 rounded-2xl text-center text-xl font-bold text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all disabled:opacity-50"
            />
          ))}
        </div> */}

        {/* Resend OTP - Now integrated in the flow */}
        {/* <div className="text-center mb-10">
          <span className="text-gray-500 text-sm">Didn't receive the OTP? </span>
          <button
            disabled={isLoading}
            className="text-[#23A9C6] text-sm font-bold hover:underline disabled:opacity-50"
            onClick={() => void handleResendOtp()}
          >
            Resend OTP
          </button>
        </div> */}

        {/* Action Button - Compact positioning */}
        <CustomButton
          variant="primary"
          disabled={isLoading}
          className="w-full h-14 text-lg rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform shadow-lg shadow-cyan-100"
          onClick={() => void handleCreateAccount()}
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <span>{intent === "login" ? "Login" : "Create Account"}</span>
          )}
        </CustomButton>
      </div>
    </div>
  );
}
